import { z } from "zod";
import { runModelTierSchema } from "./runs";

/**
 * Project policies (PS7, part 1): the owner-managed rules for approvals, spend, and agents.
 * This part stores and audits every field. Enforcement for each field lands in later parts, and
 * until it does the settings page labels the field "Recorded only". Any member may read the
 * policy; only an owner may change it.
 */
export const modelAccessSchema = z.enum(["byok", "credits"]);
export type ModelAccess = z.infer<typeof modelAccessSchema>;

export const projectPoliciesSchema = z.object({
  projectId: z.uuid(),
  requireDeployApproval: z.boolean(),
  productionTwoApprovers: z.boolean(),
  productionActionTypes: z.array(z.string().min(1)),
  approvalExpiryHours: z.number().int().min(1),
  noSelfApproval: z.boolean(),
  pauseAtCap: z.boolean(),
  alertThresholds: z.array(z.number().int().min(1).max(100)),
  modelAccess: modelAccessSchema,
  allowedModels: z.array(runModelTierSchema),
  readOnlyDefault: z.boolean(),
  maxParallelRuns: z.number().int().min(1),
  sandboxMinutes: z.number().int().min(1),
});
export type ProjectPolicies = z.infer<typeof projectPoliciesSchema>;

export const updateProjectPoliciesSchema = z.object({
  requireDeployApproval: z.boolean(),
  productionTwoApprovers: z.boolean(),
  productionActionTypes: z.array(z.string().trim().min(1).max(120)).max(20),
  approvalExpiryHours: z.number().int().min(1).max(720),
  noSelfApproval: z.boolean(),
  pauseAtCap: z.boolean(),
  alertThresholds: z.array(z.number().int().min(1).max(100)).max(5),
  modelAccess: modelAccessSchema,
  allowedModels: z.array(runModelTierSchema).min(1),
  readOnlyDefault: z.boolean(),
  /**
   * Absent means "unchanged". The agents form previously always sent this, so
   * saving any other policy field wrote the project's concurrent run limit into the
   * saved row — after which later project-level limit changes stopped flowing
   * through, because the policy had pinned its own copy.
   */
  maxParallelRuns: z.number().int().min(1).max(100).optional(),
  sandboxMinutes: z.number().int().min(1).max(1440),
});
export type UpdateProjectPolicies = z.infer<typeof updateProjectPoliciesSchema>;
